import { isDefined } from "@clipboard-health/util-ts";
import { useState } from "react";

import { applyPlatformAwareInteractionStyles } from "../../../ui-theme";
import { mergeSxProps } from "../../lib/mergeSxProps";
import { LoadingButton, type LoadingButtonProps } from "../LoadingButton";

function preventContextMenu(event: Event) {
  event.preventDefault();
  event.stopPropagation();
}

export interface HoldToConfirmButtonProps extends Omit<LoadingButtonProps, "variant"> {
  /**
   * Success callback
   */
  onConfirm: () => void;
  /**
   * This is called when the user starts to confirm the action.
   * Use e.g for adding haptics feedback to the user when they start to confirm the action.
   */
  onConfirmStart?: () => void;

  /**
   * This is called when the user stops / completes confirming the action.
   * Use e.g for adding haptics feedback to the user when they stop confirming the action (both completing and cancelling).
   */
  onConfirmEnd?: () => void;
  /**
   * How long the user needs to hold the button to confirm the action.
   */
  confirmDurationInMs?: number;
  /**
   * Custom styles to overlay on the button. These are merged with the internal
   * hold-to-confirm animation styles, allowing callers to override colors, etc.
   */
  sx?: LoadingButtonProps["sx"];
}

export function HoldToConfirmButton(props: HoldToConfirmButtonProps) {
  const {
    isLoading,
    onConfirm,
    onConfirmStart,
    onConfirmEnd,
    confirmDurationInMs = 2000,
    LinkComponent,
    children,
    size = "large",
    sx,
    ...restProps
  } = props;
  const [confirmed, setConfirmed] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | undefined>();

  const inProgress = isDefined(timeoutId);

  const onTouchStart = (event: React.TouchEvent | React.MouseEvent) => {
    preventContextMenu(event.nativeEvent);

    if (inProgress) {
      return;
    }

    setConfirmed(false);

    onConfirmStart?.();

    const timeoutId = setTimeout(() => {
      setTimeoutId(undefined);
      clearTimeout(timeoutId);

      onConfirmEnd?.();
      onConfirm();

      setConfirmed(true);
    }, confirmDurationInMs);
    setTimeoutId(timeoutId);
  };

  const onTouchEnd = (event: React.TouchEvent | React.MouseEvent) => {
    preventContextMenu(event.nativeEvent);
    clearTimeout(timeoutId);

    setTimeoutId(undefined);

    onConfirmEnd?.();
  };

  return (
    <LoadingButton
      {...restProps}
      LinkComponent={LinkComponent}
      variant="contained"
      size={size}
      sx={mergeSxProps((theme) => {
        const backgroundPosition = inProgress || confirmed ? "0%" : "100%";
        const completedColor = theme.palette.components?.holdToConfirmButton.confirmed;
        const remainingColor = theme.palette.components?.holdToConfirmButton.default;
        const background = `linear-gradient(to right, ${completedColor} 50%, ${remainingColor} 50%)`;
        const transition =
          inProgress || confirmed ? `background-position ${confirmDurationInMs}ms linear` : "none";
        const style = {
          background,
          backgroundPosition,
          backgroundSize: "200%",
          transition,
        };
        return {
          ...style,
          ...applyPlatformAwareInteractionStyles({
            hoverStyle: style,
          }),
        };
      }, sx)}
      isLoading={isLoading}
      onTouchStart={onTouchStart}
      onMouseDown={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseUp={onTouchEnd}
      onContextMenu={(event) => {
        preventContextMenu(event.nativeEvent);
      }}
    >
      {children}
    </LoadingButton>
  );
}
